import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader } from 'lucide-react';

/* ── Page de retour après paiement SlickPay ─────────────────────
   SlickPay redirige vers :
   /payment/success?shopId=...&pack=...&period=...
   (que le paiement ait réussi ou été annulé — SlickPay utilise
   toujours la même URL de retour, le webhook confirme le succès)
────────────────────────────────────────────────────────────────── */
const PaymentReturn = () => {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const shopId  = searchParams.get('shopId');
  const packId  = searchParams.get('pack');
  const period  = searchParams.get('period');

  /* On ne sait pas si c'est un succès ou une annulation à ce stade —
     le webhook Cloudflare met à jour Firestore de façon asynchrone.
     On affiche un message d'attente puis on redirige. */
  const [status, setStatus] = useState('pending'); // pending | redirecting

  useEffect(() => {
    /* Attendre 3s pour laisser le webhook le temps d'agir, puis rediriger */
    const t = setTimeout(() => {
      setStatus('redirecting');
      setTimeout(() => {
        if (shopId) {
          navigate('/merchant', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 1500);
    }, 3000);
    return () => clearTimeout(t);
  }, [shopId, navigate]);

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '1.2rem',
      padding: '2rem',
      background: 'var(--bg)',
    }}>
      {/* Icône animée */}
      <div style={{
        width: 72, height: 72,
        borderRadius: '50%',
        background: status === 'redirecting'
          ? 'rgba(0,201,167,0.12)'
          : 'rgba(99,102,241,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.4s ease',
      }}>
        {status === 'redirecting'
          ? <CheckCircle2 size={36} color="#00c9a7" strokeWidth={1.8}/>
          : <Loader size={36} color="var(--primary)" strokeWidth={1.8}
              style={{ animation: 'spin 1s linear infinite' }}/>
        }
      </div>

      {/* Texte */}
      <div style={{ textAlign: 'center', maxWidth: 320 }}>
        <div style={{
          fontWeight: 800, fontSize: '1.1rem',
          color: 'var(--text)', marginBottom: '0.5rem',
        }}>
          {status === 'redirecting'
            ? 'Retour en cours…'
            : 'Traitement en cours…'
          }
        </div>
        <div style={{
          fontSize: '0.82rem', color: 'var(--text-2)', lineHeight: 1.5,
        }}>
          {status === 'redirecting'
            ? 'Vous allez être redirigé vers votre tableau de bord.'
            : 'Votre paiement est en cours de vérification. Vous serez redirigé automatiquement.'
          }
        </div>
      </div>

      {/* Info pack */}
      {packId && (
        <div style={{
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--r-lg)',
          padding: '0.75rem 1.25rem',
          fontSize: '0.78rem', color: 'var(--text-2)',
          boxShadow: 'var(--neu-sm)',
          textAlign: 'center',
        }}>
          Pack <strong style={{ color: 'var(--primary)', textTransform: 'capitalize' }}>{packId}</strong>
          {' · '}
          {period === 'yearly' ? '1 an' : '1 mois'}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:scale(.8)} to{opacity:1;transform:scale(1)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default PaymentReturn;